import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Trash2, Undo2, AlertTriangle, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";

export default function PendingDeletionsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/pending-deletions`);
      setData(res.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleForceDelete = async (userId) => {
    if (!window.confirm("Permanently delete this user immediately? This cannot be undone.")) return;
    setActionId(userId);
    try {
      await axios.post(`${API}/admin/pending-deletions/${userId}/force`);
      toast.success("User permanently deleted");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActionId(null); }
  };

  const handleCancel = async (userId) => {
    if (!window.confirm("Cancel deletion and restore this user's account?")) return;
    setActionId(userId);
    try {
      await axios.post(`${API}/admin/pending-deletions/${userId}/cancel`);
      toast.success("Deletion cancelled, account restored");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActionId(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-xs text-slate-400 p-4">Failed to load</p>;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4" data-testid="pending-deletions-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-400" />
          Pending Deletions
        </h3>
        <span className="text-xs font-mono text-red-500 bg-red-50 px-2 py-0.5 rounded-full" data-testid="deletion-count">
          {data.count}
        </span>
      </div>

      {data.count === 0 ? (
        <p className="text-xs text-slate-300 py-4 text-center">No accounts pending deletion</p>
      ) : (
        <div className="space-y-2">
          {data.pending.map(u => (
            <div key={u.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100" data-testid={`deletion-row-${u.hashed_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700">{u.name_hint}</span>
                  <span className="text-[10px] text-slate-400 font-mono">{u.email_hint}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-slate-400">Requested: {new Date(u.requested_at).toLocaleDateString()}</span>
                  <span className="text-[10px] text-red-400">Deletes: {u.scheduled_deletion}</span>
                </div>
                {u.reason && <span className="text-[10px] text-amber-500 mt-0.5 block">Reason: {u.reason}</span>}
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button
                  onClick={() => handleCancel(u.user_id)}
                  disabled={actionId === u.user_id}
                  className="p-1.5 text-sky-500 hover:bg-sky-50 rounded-lg transition disabled:opacity-50"
                  title="Cancel deletion"
                  data-testid={`cancel-delete-${u.hashed_id}`}
                >
                  {actionId === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => handleForceDelete(u.user_id)}
                  disabled={actionId === u.user_id}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                  title="Force delete now"
                  data-testid={`force-delete-${u.hashed_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
